/**
 * FUNNEL CONTRACT Layer 2 - sole browser emitter for page_view + add_to_cart + begin_checkout.
 * Layer 3 - cart attributes from ild_acq at ATC (not URL); gap-fills _gclid/_gbraid/_wbraid
 * only, never overwriting values ild-click-id-capture already wrote for this landing.
 * funnel-landing v19 - CTA opens the theme cart drawer instead of redirecting to /checkout.
 * Drawer assets are lazy-loaded on first ATC (manifest in window.__FUNNEL_DRAWER, emitted by
 * snippets/funnel-cart-drawer-support.liquid); begin_checkout fires on the drawer's real
 * Check out click, so it once again means "shopper is heading to checkout".
 * v18 - GA4-only ATC/begin_checkout (AW-870900097 ATC/BC conversion actions
 * removed 2026-07-04, GA4-import Secondary actions used instead); single-fire dedupe by
 * action (not per-listener); cart attribute gap-fill instead of lossy gclid||gbraid||wbraid merge.
 */
(function () {
  'use strict';

  var GA4_ID = 'G-XE6CZ5DL31';

  if (window.__funnelAtcBound) return;
  window.__funnelAtcBound = true;

  function priceToDec(cents) {
    return parseFloat(((cents || 0) / 100).toFixed(2));
  }

  function readAcq() {
    if (window.ILD_ACQ && typeof window.ILD_ACQ === 'object' && Object.keys(window.ILD_ACQ).length) {
      return window.ILD_ACQ;
    }
    try {
      return JSON.parse(localStorage.getItem('ild_acq') || '{}');
    } catch (e) {
      return {};
    }
  }

  function itemFromLine(line) {
    return {
      item_id: String(line.product_id || line.id || ''),
      item_name: line.product_title || line.title || '',
      item_variant: line.variant_title || '',
      price: priceToDec(line.final_price || line.price || 0),
      quantity: line.quantity || 1,
    };
  }

  function attachCartAttributes() {
    var acq = readAcq();
    var ildAcqId = '';
    try {
      ildAcqId = btoa(JSON.stringify(acq)).slice(0, 240);
    } catch (e) {
      ildAcqId = '';
    }

    // Layer 3 (contract section 4): sourced from ild_acq, not the live URL. Click-ID
    // fields are gap-fill ONLY - ild-click-id-capture.liquid already wrote the newest
    // gclid/gbraid/wbraid as separate cart attributes on this landing (ild_acq is
    // first-touch and may be staler), so we read the current cart first and only add
    // a click-ID field here if that specific attribute is still empty. Never merge
    // gclid/gbraid/wbraid into one field - Google Ads distinguishes them.
    return fetch('/cart.js')
      .then(function (response) {
        return response.json();
      })
      .catch(function () {
        return { attributes: {} };
      })
      .then(function (cart) {
        var existing = (cart && cart.attributes) || {};
        var attributes = {
          fbclid: acq.fbclid || '',
          utm_source: acq.utm_source || '',
          utm_campaign: acq.utm_campaign || '',
          utm_term: acq.utm_term || '',
          utm_medium: acq.utm_medium || '',
          landing_page: acq.landing_page || '',
          first_source: acq.first_source || '',
          ild_acq_ts: acq.ts ? String(acq.ts) : '',
          ild_acq_id: ildAcqId,
        };
        ['_gclid', '_gbraid', '_wbraid'].forEach(function (attrKey) {
          var acqKey = attrKey.slice(1);
          if (!existing[attrKey] && acq[acqKey]) {
            attributes[attrKey] = acq[acqKey];
          }
        });
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ attributes: attributes }),
        });
      })
      .then(function (response) {
        return response.json();
      });
  }

  function addToCart(variantId) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
      }),
    }).then(function (response) {
      return response.json().then(function (payload) {
        if (!response.ok || payload.status) throw payload;
        return payload;
      });
    });
  }

  function fireKlaviyoAddedToCart(lineItem) {
    var _learnq = window._learnq = window._learnq || [];
    var price = priceToDec(lineItem.final_price || lineItem.price || 0);
    var productName = lineItem.title || lineItem.product_title || '';
    var productId = String(lineItem.product_id || lineItem.id || '');
    var imageUrl = (lineItem.featured_image && lineItem.featured_image.url) ? lineItem.featured_image.url : '';
    var productUrl = lineItem.url ? ('https://' + location.host + lineItem.url) : '';
    _learnq.push(['track', 'Added to Cart', {
      $value: price,
      AddedItemProductName: productName,
      AddedItemProductID: productId,
      AddedItemSKU: lineItem.sku || '',
      AddedItemCategories: lineItem.product_type ? [lineItem.product_type] : [],
      AddedItemImageURL: imageUrl,
      AddedItemURL: productUrl,
      AddedItemPrice: price,
      AddedItemQuantity: lineItem.quantity || 1,
      ItemNames: [productName],
      CheckoutURL: 'https://' + location.host + '/checkout',
      Items: [{
        ProductID: productId,
        SKU: lineItem.sku || '',
        ProductName: productName,
        Quantity: lineItem.quantity || 1,
        ItemPrice: price,
        RowTotal: parseFloat((price * (lineItem.quantity || 1)).toFixed(2)),
        ProductURL: productUrl,
        ImageURL: imageUrl,
        Categories: lineItem.product_type ? [lineItem.product_type] : [],
      }]
    }]);
  }

  function fireAddToCartGtag(lineItem) {
    if (typeof gtag !== 'function') return;
    var currency =
      lineItem.currency ||
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
      'USD';
    var value = priceToDec(lineItem.final_price || lineItem.price || 0);
    var items = [itemFromLine(lineItem)];
    // GA4 event only (send_to GA4_ID). The legacy AW-870900097 "Add To Cart signal"
    // conversion action was removed from Google Ads 2026-07-04; GA4-import add_to_cart
    // is now the Secondary action there, so no AW conversion ping is fired here.
    gtag('event', 'add_to_cart', {
      send_to: GA4_ID,
      currency: currency,
      value: value,
      items: items,
    });
  }

  // --- Cart drawer (lazy) -------------------------------------------------
  // bare.liquid strips the theme layout for PSI, so the drawer (markup, CSS,
  // JS) does not exist here until the first ATC. Everything below loads it on
  // demand: not one extra request happens before the shopper clicks a CTA.
  var drawerBoot = null;
  var lastCart = null;

  function loadScriptOnce(src) {
    return new Promise(function (resolve, reject) {
      if (document.querySelector('script[src="' + src + '"]')) return resolve();
      var s = document.createElement('script');
      s.src = src;
      s.onload = resolve;
      s.onerror = function () { reject(new Error('failed to load ' + src)); };
      document.head.appendChild(s);
    });
  }

  function loadScriptsInOrder(srcs) {
    return srcs.reduce(function (p, src) {
      return p.then(function () { return loadScriptOnce(src); });
    }, Promise.resolve());
  }

  // bare.liquid sets the same rem root as theme.liquid (62.5% * body scale),
  // so theme CSS - fetched files AND the inline styles inside the drawer's
  // section HTML - renders at intended sizes with no transform.
  function injectThemeCss(href, asBaseLayer) {
    return fetch(href)
      .then(function (r) { return r.text(); })
      .then(function (text) {
        var scaled = text;
        var style = document.createElement('style');
        style.setAttribute('data-funnel-drawer-css', href);
        // base.css goes into a cascade layer: layered styles always lose to the
        // funnel page's own (unlayered) styles, so it can style the drawer but
        // can never restyle the page content.
        style.textContent = asBaseLayer ? '@layer funnel-drawer-base{' + scaled + '}' : scaled;
        document.head.appendChild(style);
      });
  }

  function fetchDrawerSection() {
    return fetch('/?sections=cart-drawer', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function ensureDrawer() {
    if (drawerBoot) return drawerBoot;
    var cfg = window.__FUNNEL_DRAWER;
    if (!cfg) return Promise.reject(new Error('no __FUNNEL_DRAWER config'));
    var cssReady = Promise.all(
      [injectThemeCss(cfg.baseCss, true)].concat(
        (cfg.css || []).map(function (href) { return injectThemeCss(href, false); })
      )
    );
    drawerBoot = loadScriptsInOrder(cfg.js || [])
      .then(fetchDrawerSection)
      .then(function (sections) {
        var holder = document.createElement('div');
        holder.innerHTML = sections['cart-drawer'] || '';
        // Scripts the drawer snippet embeds (cart.js, quantity-popover.js,
        // ild-shopify-cart-events.js, il-freeship-nudge.js, whatever gets added
        // later) do not execute via innerHTML - collect and load them for real.
        var embedded = [].map.call(holder.querySelectorAll('script[src]'), function (s) {
          return s.getAttribute('src');
        });
        [].forEach.call(holder.querySelectorAll('script'), function (s) {
          s.parentNode.removeChild(s);
        });
        document.body.appendChild(holder);
        return loadScriptsInOrder(embedded);
      })
      .then(function () { return loadScriptOnce(cfg.drawerJs); })
      .then(function () { return cssReady; })
      .then(function () {
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          subscribe(PUB_SUB_EVENTS.cartUpdate, function (ev) {
            if (ev && ev.cartData) lastCart = ev.cartData;
          });
        }
        return document.querySelector('cart-drawer');
      });
    return drawerBoot;
  }

  function openDrawer() {
    return ensureDrawer().then(function (drawer) {
      if (!drawer) throw new Error('cart-drawer missing after boot');
      // Keep the begin_checkout snapshot current even when the ATC came from
      // il-funnel.js (its pipeline never touches our lastCart).
      fetch('/cart.js').then(function (r) { return r.json(); }).then(function (c) { lastCart = c; }).catch(function () {});
      if (!drawer.__funnelOpened) {
        // First boot: the section was fetched after this ATC, so it is current.
        drawer.__funnelOpened = true;
        drawer.classList.remove('is-empty');
        drawer.open();
        return;
      }
      // Later ATCs: re-render with fresh cart contents (renderContents re-opens).
      return fetchDrawerSection().then(function (sections) {
        drawer.renderContents({ sections: sections });
      });
    });
  }
  // Product funnels' il-funnel.js CTA handler hands off here (it wins the click
  // via capture on #final-atc-btn) so every funnel opens the same drawer.
  window.__funnelOpenDrawer = openDrawer;

  function runAtcPipeline(variantId) {
    var lineItem;
    return addToCart(variantId)
      .then(function (payload) {
        lineItem = payload;
        return attachCartAttributes();
      })
      .then(function (cartJson) {
        if (cartJson && cartJson.items) lastCart = cartJson;
        fireAddToCartGtag(lineItem);
        fireKlaviyoAddedToCart(lineItem);
        return openDrawer();
      })
      .catch(function (error) {
        // Drawer boot failed (network, blocked asset): fall back to the cart
        // page so the shopper is never stranded on the landing page.
        if (drawerBoot) {
          console.error('[funnel] drawer failed, falling back to /cart:', error);
          window.location.href = '/cart';
          return;
        }
        throw error;
      });
  }

  function fixBrokenStarsImgs() {
    document.querySelectorAll('img[src*="<span"]').forEach(function (img) {
      var span = document.createElement('span');
      span.setAttribute('role', 'img');
      span.setAttribute('aria-label', img.getAttribute('alt') || '4.9 star rating');
      span.style.cssText =
        'color:#f59e0b;font-size:28px;line-height:48px;letter-spacing:2px;display:inline-block;width:260px;height:48px;vertical-align:middle;';
      span.textContent = '★★★★★';
      img.replaceWith(span);
    });
  }

  function optimizeHeroMedia() {
    fixBrokenStarsImgs();

    var lcpImg = document.querySelector(
      '.funnel-landing-content img[src*="OalMiracle_acid_chart"], .funnel-landing-content img[src*="OralMiracle_daily_acid_attack"]'
    );
    if (lcpImg) {
      lcpImg.setAttribute('fetchpriority', 'high');
      lcpImg.setAttribute('loading', 'eager');
      lcpImg.setAttribute('decoding', 'async');
    }

    document
      .querySelectorAll(
        '.funnel-landing-content img[src*="OralMiracle_bottle"], .funnel-landing-content img[alt*="OralMiracle"]'
      )
      .forEach(function (heroImg) {
        if (heroImg === lcpImg) return;
        heroImg.setAttribute('loading', 'lazy');
        heroImg.setAttribute('decoding', 'async');
        if (!heroImg.getAttribute('width')) heroImg.setAttribute('width', '340');
        if (!heroImg.getAttribute('height')) heroImg.setAttribute('height', '340');
      });
  }

  function bindVideos() {
    document.querySelectorAll('.funnel-landing-content video, .video-section video').forEach(function (video) {
      if (video.dataset.funnelVideoBound) return;
      video.dataset.funnelVideoBound = '1';
      if (!video.hasAttribute('playsinline')) video.setAttribute('playsinline', '');

      // Hero UGC uses native controls + page preload="metadata". Forcing preload=none
      // and a bare play() click handler breaks the control bar (no load() first).
      if (video.hasAttribute('controls')) return;

      video.preload = 'none';
      video.addEventListener('click', function () {
        if (!video.paused) return;
        function attemptPlay() {
          var p = video.play();
          if (p && typeof p.catch === 'function') p.catch(function () {});
        }
        if (video.readyState >= 2) {
          attemptPlay();
          return;
        }
        video.addEventListener('canplay', attemptPlay, { once: true });
        video.load();
      });
    });
  }

  // Single-fire guarantee: one add_to_cart / begin_checkout per real ATC action,
  // regardless of which UI element triggered it (main CTA, sticky cart, drawer, etc).
  // Dedupe is by ACTION (this one module-level in-flight flag), not by listener/button,
  // since the delegated click handler already covers every ATC-capable element with a
  // single listener.
  var atcInFlight = false;

  document.addEventListener('click', function (e) {
    // Clicks inside the drawer are the drawer's own (sample bridge buttons carry
    // data-variant-id too - il-freeship-nudge.js handles those; matching them here
    // would double-add the item).
    if (e.target.closest('cart-drawer')) return;
    var btn =
      e.target.closest('[data-action="atc"], [data-action="sample"]') ||
      e.target.closest('[data-variant-id]');
    if (!btn) return;

    var variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;

    e.preventDefault();
    if (atcInFlight || btn.dataset.atcBusy === '1') return;

    atcInFlight = true;
    btn.dataset.atcBusy = '1';
    var prevOpacity = btn.style.opacity;
    btn.style.opacity = '0.6';

    runAtcPipeline(variantId)
      .catch(function (error) {
        console.error('[funnel] add to cart failed:', error);
        alert((error && error.description) || 'Error adding product to cart. Please try again.');
      })
      .finally(function () {
        atcInFlight = false;
        btn.dataset.atcBusy = '';
        btn.style.opacity = prevOpacity || '';
      });
  });

  // begin_checkout fires when the shopper actually leaves for checkout via the
  // drawer's Check out button. Same beacon-style gtag transport as v18: the
  // event send does not race the form submission, no gating needed.
  document.addEventListener(
    'click',
    function (e) {
      if (!e.target.closest('#CartDrawer-Checkout')) return;
      if (typeof gtag !== 'function') return;
      var cart = lastCart || {};
      gtag('event', 'begin_checkout', {
        send_to: GA4_ID,
        currency: cart.currency || 'USD',
        value: priceToDec(cart.total_price || 0),
        items: (cart.items || []).map(itemFromLine),
      });
    },
    true
  );

  function fireKlaviyoViewedProduct() {
    var _learnq = window._learnq = window._learnq || [];
    _learnq.push(['track', 'Viewed Product', {
      ProductName: 'OralMiracle Enamel Repair Mouthwash | nHAP Remineralizing Rinse',
      ProductID: '7846339078',
      SKU: 'oralmiracle-mineral-rinse',
      Categories: ['Oral Care', 'Remineralizing Mouthwash'],
      ImageURL: 'https://cdn.shopify.com/s/files/1/1410/7814/files/OralMiracle_Bottle.webp?v=1772324241',
      URL: window.location.href,
      Brand: 'immunizeLABS',
      Price: 50.00,
      CompareAtPrice: 50.00
    }]);
  }

  function firePageViewOnce() {
    if (window.__funnelPageViewFired) return;
    window.__funnelPageViewFired = true;
    if (typeof gtag !== 'function') return;
    gtag('event', 'page_view', {
      page_location: window.location.href,
      page_title: document.title,
    });
  }

  function initDomOptimizations() {
    firePageViewOnce();
    fireKlaviyoViewedProduct();
    optimizeHeroMedia();
    bindVideos();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDomOptimizations, { once: true });
  } else {
    initDomOptimizations();
  }

})();
